/**
 * MSAL.js wrapper — single PublicClientApplication shared process-wide.
 *
 * Design notes:
 *   - Configured via Vite env vars (VITE_ENTRA_TENANT_ID, VITE_ENTRA_CLIENT_ID,
 *     VITE_ENTRA_SCOPE, VITE_USE_REAL_AUTH). If VITE_USE_REAL_AUTH is falsy,
 *     the module stays un-initialised and `acquireToken()` returns null so the
 *     existing X-Dev-User header path keeps working for local dev + demos.
 *   - Uses redirect flow rather than popup — popups are blocked in many
 *     IT-managed corporate browser profiles.
 *   - `acquireToken()` asks silently first; on `InteractionRequiredAuthError`
 *     it falls back to redirect, which leaves a promise that never resolves
 *     (the page reloads via MSAL's hash redirect). Callers should treat a null
 *     return as "auth not configured" and a throw as "redirect started".
 */
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
} from "@azure/msal-browser";

const TENANT = import.meta.env.VITE_ENTRA_TENANT_ID ?? "";
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID ?? "";
const SCOPE = import.meta.env.VITE_ENTRA_SCOPE ?? "";
export const USE_REAL_AUTH =
  (import.meta.env.VITE_USE_REAL_AUTH ?? "false").toString().toLowerCase() ===
    "true" &&
  !!TENANT &&
  !!CLIENT_ID &&
  !!SCOPE;

let pca: PublicClientApplication | null = null;
let initPromise: Promise<void> | null = null;

function buildConfig(): Configuration {
  return {
    auth: {
      clientId: CLIENT_ID,
      authority: `https://login.microsoftonline.com/${TENANT}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
  };
}

export async function initMsal(): Promise<PublicClientApplication | null> {
  if (!USE_REAL_AUTH) return null;
  if (pca) return pca;
  if (!initPromise) {
    const app = new PublicClientApplication(buildConfig());
    initPromise = app
      .initialize()
      .then(async () => {
        // Handle return from redirect.
        const resp = await app.handleRedirectPromise();
        if (resp?.account) {
          app.setActiveAccount(resp.account);
        } else {
          const existing = app.getAllAccounts();
          if (existing.length > 0) app.setActiveAccount(existing[0]);
        }
        pca = app;
      })
      .catch((err) => {
        console.error("msal init failed", err);
        throw err;
      });
  }
  await initPromise;
  return pca;
}

export function currentAccount(): AccountInfo | null {
  return pca?.getActiveAccount() ?? null;
}

/**
 * Try to establish a session WITHOUT sending the user to Entra.
 *
 * This is the question the landing gate actually needs answered: not "do we
 * have a cached account" (a first visit from an entitled user has none
 * either) but "is there a session we can silently use". Returning null means
 * genuinely signed out, which is the only case that should be shown a
 * sign-up page.
 *
 * Never throws and never redirects — a failure here is an answer, not an
 * error. ssoSilent can also fail when the browser has several Entra sessions
 * and no hint to choose between them; that lands on the same screen, where
 * the sign-in button resolves it interactively.
 */
/**
 * Why the last silent-SSO attempt did not produce a session.
 *
 * This exists because of a real ambiguity in the first week's data: EVERY
 * beta signup arrived as "signed-out" and not one as "not-entitled", which
 * has two very different readings. Either people genuinely have no Entra
 * session, or ssoSilent is failing for everyone — and the second means
 * ENTITLED users are being shown the gate too, and paying a needless extra
 * click to get in.
 *
 * MSAL's own error code separates them: `login_required` /
 * `interaction_required` mean there is genuinely nothing to use, while a
 * `monitor_window_timeout` or an iframe/frame error means the browser
 * blocked the silent flow that WOULD have worked. Recording the code is what
 * turns "we cannot tell" into an answer.
 *
 * Short and enumerated on purpose — it is sent to the backend as `source`,
 * which is a 32-character column, and it must never carry a message that
 * could contain user detail.
 */
export type SsoFailure =
  | "no_session" // MSAL says there is nothing to use
  | "blocked" // the browser stopped a flow that could have worked
  | "no_account" // succeeded but returned no account
  | "unknown";

let lastSsoFailure: SsoFailure | null = null;

export function ssoFailureReason(): SsoFailure | null {
  return lastSsoFailure;
}

/** MSAL codes that mean "genuinely signed out" rather than "we were stopped". */
const NO_SESSION_CODES = new Set([
  "login_required",
  "interaction_required",
  "consent_required",
  "no_account_error",
  "silent_sso_error",
]);

export async function trySilentSso(): Promise<AccountInfo | null> {
  lastSsoFailure = null;
  const app = await initMsal();
  if (!app) {
    lastSsoFailure = "no_session";
    return null;
  }
  try {
    const res = await app.ssoSilent({ scopes: [SCOPE] });
    if (res.account) {
      app.setActiveAccount(res.account);
      return res.account;
    }
    lastSsoFailure = "no_account";
  } catch (err) {
    const code = String(
      (err as { errorCode?: string })?.errorCode ?? "",
    ).toLowerCase();
    if (NO_SESSION_CODES.has(code)) {
      lastSsoFailure = "no_session";
    } else if (code) {
      // Timeouts, blocked iframes, hidden-frame failures. The distinction
      // that matters: this user might well have been let straight in.
      lastSsoFailure = "blocked";
      console.warn("ssoSilent blocked:", code);
    } else {
      lastSsoFailure = "unknown";
    }
  }
  return null;
}

export async function login(opts: { selectAccount?: boolean } = {}): Promise<void> {
  const app = await initMsal();
  if (!app) return;
  // `selectAccount` shows Microsoft's account picker. Without it the existing
  // session is reused silently — right for a normal sign-in, and a loop for
  // someone signed in with the wrong account trying to switch.
  await app.loginRedirect({
    scopes: [SCOPE],
    ...(opts.selectAccount ? { prompt: "select_account" } : {}),
  });
}

export async function logout(): Promise<void> {
  const app = await initMsal();
  if (!app) return;
  await app.logoutRedirect();
}

export interface AcquiredToken {
  token: string;
  account: AccountInfo;
}

/**
 * Return a valid access token for `SCOPE`, or null if real-auth is disabled.
 * On silent failure we trigger an interactive redirect; the call then never
 * resolves (the page navigates away).
 */
export async function acquireToken(): Promise<AcquiredToken | null> {
  const app = await initMsal();
  if (!app) return null;
  const account = app.getActiveAccount() ?? app.getAllAccounts()[0];
  if (!account) {
    await app.loginRedirect({ scopes: [SCOPE] });
    return await new Promise<AcquiredToken>(() => {}); // unreachable
  }
  try {
    const result: AuthenticationResult = await app.acquireTokenSilent({
      account,
      scopes: [SCOPE],
    });
    return { token: result.accessToken, account };
  } catch (err) {
    if (
      err instanceof InteractionRequiredAuthError ||
      err instanceof BrowserAuthError
    ) {
      await app.acquireTokenRedirect({ account, scopes: [SCOPE] });
      return await new Promise<AcquiredToken>(() => {}); // unreachable
    }
    throw err;
  }
}
